import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui';
import { api, apiErrorCode, formatApiError, type ConnectApproveIn } from '@/lib/api';
import { AuthLayout } from './AuthLayout';

/**
 * "Claude wants to connect to PriceQ" — the consent step of the AI-app OAuth
 * flow (spec 2026-11-02 §4.1). /oauth/authorize checked the client and its
 * redirect address and sent the browser here with every parameter; this page
 * is behind the normal login (MFA included) and posts the person's choice to
 * /api/v1/connect/approve, which checks client and address again and answers
 * with where to send the browser next.
 *
 * `navigateTo` leaves the app (the redirect goes to the AI app's own site);
 * tests pass a spy instead of touching window.location.
 */
export function ConnectAuthorizePage({
  navigateTo = (url: string) => window.location.assign(url),
}: {
  navigateTo?: (url: string) => void;
}) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [failed, setFailed] = useState<string | null>(null);

  const clientId = params.get('client_id') ?? '';
  const request: Omit<ConnectApproveIn, 'decision'> = {
    client_id: clientId,
    redirect_uri: params.get('redirect_uri') ?? '',
    code_challenge: params.get('code_challenge') ?? '',
    code_challenge_method: 'S256',
    scope: params.get('scope'),
    state: params.get('state'),
    resource: params.get('resource'),
  };
  const complete =
    !!clientId && !!request.redirect_uri && !!request.code_challenge && params.get('code_challenge_method') === 'S256';

  const client = useQuery({
    queryKey: ['connect-client', clientId],
    queryFn: () => api.connect.client(clientId),
    enabled: complete,
  });
  const tenant = useQuery({ queryKey: ['tenant-me'], queryFn: () => api.tenants.me(), enabled: complete });

  const decide = useMutation({
    mutationFn: (decision: 'allow' | 'deny') => api.connect.approve({ ...request, decision }),
    onSuccess: (res) => navigateTo(res.redirect_to),
    onError: (err: unknown) => {
      const code = apiErrorCode(err);
      if (code === 'not_available') setFailed(t('connect.notAvailable'));
      else if (code === 'read_only') setFailed(t('connect.lookAlong'));
      else setFailed(formatApiError(err, t('common.error')));
    },
  });

  if (!complete) {
    return (
      <AuthLayout title={t('connect.incompleteTitle')}>
        <p className="text-text-secondary" style={{ fontSize: 13.5, margin: 0 }}>
          {t('connect.incomplete')}
        </p>
      </AuthLayout>
    );
  }

  const app = client.data?.client_name ?? t('connect.anApp');
  return (
    <AuthLayout title={t('connect.title', { app })}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 13.5 }}>
        {client.data && (
          <p className={client.data.verified ? 'text-text-secondary' : 'text-danger'} style={{ margin: 0 }}>
            {client.data.verified
              ? t('connect.returnsTo', { host: client.data.redirect_host })
              : t('connect.unverified', { host: client.data.redirect_host })}
          </p>
        )}
        {tenant.data?.name && (
          <p className="text-text-secondary" style={{ margin: 0 }}>
            {t('connect.forOrg', { org: tenant.data.name })}
          </p>
        )}
        <div>
          <div className="text-text-primary" style={{ fontWeight: 500, marginBottom: 4 }}>
            {t('connect.mayTitle')}
          </div>
          <ul className="text-text-secondary" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li>{t('connect.mayRead')}</li>
            <li>{t('connect.maySetup')}</li>
            <li>{t('connect.mayAdmin')}</li>
          </ul>
        </div>
        <div>
          <div className="text-text-primary" style={{ fontWeight: 500, marginBottom: 4 }}>
            {t('connect.mayNotTitle')}
          </div>
          <ul className="text-text-secondary" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li>{t('connect.mayNotAccount')}</li>
          </ul>
        </div>
        <p className="text-text-muted" style={{ fontSize: 12.5, margin: 0 }}>
          {t('connect.wrongOrg')}
        </p>
        {failed && (
          <p className="text-danger" style={{ margin: 0 }} role="alert">
            {failed}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => decide.mutate('allow')} disabled={decide.isPending}>
            {t('connect.allow')}
          </Button>
          <Button variant="ghost" onClick={() => decide.mutate('deny')} disabled={decide.isPending}>
            {t('connect.deny')}
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
